import { yupResolver } from '@hookform/resolvers/yup'
import {
  Button,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useAppDispatch } from 'app/hooks'
import { UIAction } from 'app/slices/UI'
import DialogConfirm from 'components/Dialogs/DialogConfirm'
import { Permission } from 'interface'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import axiosClient from 'utils/axiosClient'
import * as yup from 'yup'

interface FormInput {
  id: string
  permission: Permission
  firstName: string
  lastName: string
}
export default function AccountRegister() {
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<FormInput>()
  const schema = yup.object({
    id: yup
      .string()
      .required('IDを入力してください。')
      .email('IDはメール形式で入力してください。'),
    firstName: yup.string().required('名を入力してください。'),
    lastName: yup.string().required('姓を入力してください。'),
  })
  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<FormInput>({
    resolver: yupResolver(schema),
    defaultValues: {
      permission: 'standard',
    },
  })
  const dispatch = useAppDispatch()
  useEffect(() => {
    dispatch(
      UIAction.setBreadcrumbs([
        {
          text: 'ホーム',
          url: '/',
        },
        {
          text: 'アカウント管理',
          url: '/account',
        },
        {
          text: '新規登録',
          url: '/account/register',
        },
      ])
    )
  }, [dispatch])
  const onSubmitForm: SubmitHandler<FormInput> = (data) => {
    setData(data)
  }
  const handleRegister = async () => {
    try {
      setLoading(true)
      await axiosClient.post('/api/system/register', data)

      enqueueSnackbar('新規作成が完了しました。', {
        variant: 'success',
      })
      navigate('/account')
    } catch (error) {
      const res = error as any
      if (res.status === 409) {
        enqueueSnackbar('既にメールアドレスまたはIDが存在しています。', {
          variant: 'error',
        })
      } else {
        enqueueSnackbar('エラーが発生しました。', {
          variant: 'error',
        })
      }
      setData(undefined)
    } finally {
      setLoading(false)
    }
  }
  return (
    <>
      <Grid container spacing={1} mt={2}>
        <Grid item xs={12}>
          <Typography variant="h4" component="h2" sx={{ borderBottom: 2 }}>
            新規登録
          </Typography>
        </Grid>
        <Grid item xs={12} ml={1}>
          <Container
            disableGutters
            component="form"
            noValidate
            autoComplete="off"
            maxWidth="sm"
            sx={{
              margin: '0',
              padding: '0 !important',
              mt: 2,
            }}
            onSubmit={handleSubmit(onSubmitForm)}
          >
            <TextField
              required
              label="ログインID"
              margin="dense"
              type={'email'}
              fullWidth
              variant="standard"
              {...register('id')}
              error={'id' in errors}
              helperText={errors.id?.message}
            />
            <Stack
              direction="row"
              spacing={2}
              alignItems="baseline"
              justifyContent="space-between"
              my={4}
            >
              <TextField
                required
                label="姓"
                margin="dense"
                fullWidth
                variant="standard"
                {...register('lastName')}
                error={'lastName' in errors}
                helperText={errors.lastName?.message}
              />
              <TextField
                required
                label="名"
                margin="dense"
                fullWidth
                variant="standard"
                {...register('firstName')}
                error={'firstName' in errors}
                helperText={errors.firstName?.message}
              />
            </Stack>
            <FormControl fullWidth className="my-6">
              <InputLabel>権限</InputLabel>
              <Select
                value={getValues('permission')}
                label="権限"
                onChange={(event) => {
                  setValue('permission', event.target.value as Permission, {
                    shouldValidate: true,
                  })
                }}
              >
                <MenuItem value={'admin'}>Admin</MenuItem>
                <MenuItem value={'subAdmin'}>Editor</MenuItem>
                <MenuItem value={'standard'}>Standard</MenuItem>
              </Select>
            </FormControl>
            <Button
              disabled={Object.keys(errors).length > 0}
              color="inherit"
              variant="outlined"
              sx={{
                px: 12,
                borderColor: '#aaa',
                borderRadius: 2,
                mt: 4,
              }}
              type="submit"
            >
              登録
            </Button>
          </Container>
        </Grid>
      </Grid>
      {data && (
        <DialogConfirm
          open={true}
          loading={loading}
          handleClose={() => setData(undefined)}
          handleSubmit={handleRegister}
          title="登録内容の確認"
          primaryButtonText="登録する"
        >
          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
          >
            <Typography
              variant="body1"
              component="h4"
              sx={{
                width: 130,
                fontWeight: 'bold',
              }}
            >
              ログインID
            </Typography>
            <Typography variant="body1" component="h4">
              {data.id}
            </Typography>
          </Stack>
          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
          >
            <Typography
              variant="body1"
              component="h4"
              sx={{
                width: 130,
                fontWeight: 'bold',
              }}
            >
              氏名
            </Typography>
            <Typography variant="body1" component="h4">
              {`${data.lastName} ${data.firstName}`}
            </Typography>
          </Stack>
        </DialogConfirm>
      )}
    </>
  )
}
